import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { PaperPlaneRight, ChatsTeardrop, Paperclip } from '@phosphor-icons/react';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { portalGetChat, portalSendChat, portalUploadChatAttachment } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import ChatAttachment from '../../components/ChatAttachment';

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

const PortalMessages = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const listRef = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await portalGetChat();
      setMessages(r.data.messages || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    pollRef.current = setInterval(load, 5000);
    return () => clearInterval(pollRef.current);
  }, [load]);
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await portalSendChat(text);
      setInput('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  const handleAttachment = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || uploading) return;
    setUploading(true);
    try {
      await portalUploadChatAttachment(file, input.trim() || undefined);
      setInput('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="portal-messages-page">
      <h1 className="text-2xl font-bold flex items-center gap-2 mb-2">
        <ChatsTeardrop className="w-7 h-7" weight="duotone" /> Messages
      </h1>
      <p className="text-sm text-muted-foreground mb-4">Chat directly with your 360 Carexport team. They typically reply within business hours.</p>

      <div className="border border-border h-[60vh] flex flex-col bg-white">
        <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-secondary/10" data-testid="portal-messages-list">
          {messages.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground mt-10">Say hi — we're listening 👋</p>
          ) : messages.map((m) => {
            const mine = m.sender_id === user?.id;
            return (
              <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap ${
                  mine ? 'bg-primary text-primary-foreground rounded-br-sm' : 'bg-white border border-border rounded-bl-sm'
                }`}>
                  {!mine && <p className="text-[10px] font-semibold opacity-70 mb-0.5">{m.sender_name}</p>}
                  {m.body}
                  {m.attachment && <ChatAttachment attachment={m.attachment} mine={mine} />}
                  <p className={`text-[9px] mt-1 ${mine ? 'opacity-70' : 'text-muted-foreground'}`}>{fmt(m.created_at)}</p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="p-2 border-t border-border bg-white flex items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            onChange={handleAttachment}
            className="hidden"
            data-testid="portal-attach-input"
          />
          <Button size="icon" variant="ghost" onClick={() => fileInputRef.current?.click()} disabled={uploading} title="Attach image or PDF" data-testid="portal-attach-btn">
            <Paperclip className="w-4 h-4" />
          </Button>
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={uploading ? 'Uploading…' : 'Type a message…'}
            rows={1}
            className="flex-1 resize-none min-h-[40px] max-h-[120px] text-sm"
            disabled={sending || uploading}
            data-testid="portal-message-input"
          />
          <Button onClick={send} disabled={sending || uploading || !input.trim()} data-testid="portal-send-btn">
            <PaperPlaneRight weight="fill" className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PortalMessages;
